import { io, Socket } from 'socket.io-client';
import { getApiBaseUrl } from '@/config/api.config';
import { authService } from './auth.service';
import {
  UserNotification,
  NewNotificationPayload,
  NotificationReadPayload,
  UnreadCountPayload,
} from '@/types/notification.types';

export interface NotificationSocketCallbacks {
  onNewNotification?: (notification: UserNotification) => void;
  onNotificationRead?: (payload: NotificationReadPayload) => void;
  onUnreadCountUpdated?: (count: number) => void;
  onAllRead?: () => void;
  onNotificationCleared?: (notificationId: string) => void;
  onNotificationDismissed?: (notificationId: string) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onReconnected?: () => void;
  onError?: (error: any) => void;
  onReconnecting?: (attempt: number) => void;
}

class NotificationSocketService {
  private socket: Socket | null = null;
  private callbacks: NotificationSocketCallbacks = {};
  private maxReconnectAttempts = 10;
  private isConnecting = false;
  private userId: string | null = null;

  // Guard against re-entrant auth:expired handling. Multiple in-flight
  // emits can each trigger a separate auth:expired event before the
  // server's disconnect actually closes the transport — without this
  // flag we'd fire parallel refresh+reconnect loops.
  private isHandlingAuthExpired = false;

  /**
   * Initialize the socket connection
   */
  connect(userId: string, callbacks: NotificationSocketCallbacks = {}): void {
    if (this.socket?.connected || this.isConnecting) {
      console.log('[NotificationSocket] Already connected or connecting');
      return;
    }

    this.userId = userId;
    this.callbacks = callbacks;
    this.isConnecting = true;

    if (!authService.isAuthenticated()) {
      console.error('[NotificationSocket] No auth session available');
      this.isConnecting = false;
      return;
    }

    const baseUrl = getApiBaseUrl();
    // Remove trailing path from base URL if present
    const socketUrl = baseUrl.replace(/\/skillzen-srv\/?$/, '');

    console.log('[NotificationSocket] Connecting to:', socketUrl);

    this.socket = io(`${socketUrl}/notifications`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup socket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[NotificationSocket] Connected');
      this.isConnecting = false;

      // Subscribe to user's notifications
      if (this.userId) {
        this.socket?.emit('subscribe', { user_id: this.userId });
      }

      this.callbacks.onConnect?.();
    });

    // Auth expired: emitted by server when the per-message guard rejects
    // a mid-session token. The socket is force-disconnected right after,
    // and Socket.IO does not auto-reconnect on server-initiated disconnects
    // (`io server disconnect`), so we refresh the cookie token here and
    // re-establish the socket — same flow as the REST 401 interceptor.
    this.socket.on('auth:expired', async (data: { reason?: string; message?: string }) => {
      if (this.isHandlingAuthExpired) {
        console.log('[NotificationSocket] 🔐 auth:expired already in flight — ignoring duplicate');
        return;
      }
      this.isHandlingAuthExpired = true;

      console.warn('[NotificationSocket] 🔐 Server signaled auth:expired:', data);

      try {
        const refreshed = await authService.refreshAccessToken();
        if (refreshed) {
          console.log('[NotificationSocket] ✅ Token refreshed after auth:expired, reconnecting...');
          const userId = this.userId;
          const callbacks = this.callbacks;
          // The server already disconnected us; tear down the old socket
          // and let connect() create a fresh one with the refreshed cookies.
          if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
          }
          this.isConnecting = false;
          if (userId) {
            this.connect(userId, callbacks);
          }
        } else {
          console.error('[NotificationSocket] ❌ Token refresh failed after auth:expired');
          this.callbacks.onError?.(new Error('Session expired'));
          authService.clearSession();
          window.location.replace('/login');
        }
      } finally {
        this.isHandlingAuthExpired = false;
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[NotificationSocket] Disconnected:', reason);
      this.callbacks.onDisconnect?.();
    });

    this.socket.on('connect_error', (error) => {
      console.error('[NotificationSocket] Connection error:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error);
    });

    // Handle new notification event.
    // The server's notificationGateway.sendNotificationToUser() emits the
    // notification object directly (not wrapped in { notification: ... }),
    // but older callers may have used the wrapped shape. Accept both.
    this.socket.on('notification:new', (payload: NewNotificationPayload | UserNotification) => {
      console.log('[NotificationSocket] New notification:', payload);
      const notification =
        (payload as NewNotificationPayload)?.notification ?? (payload as UserNotification);
      if (notification && (notification as UserNotification)._id) {
        this.callbacks.onNewNotification?.(notification as UserNotification);
      } else {
        console.warn('[NotificationSocket] notification:new payload has unexpected shape:', payload);
      }
    });

    // Handle notification read event
    this.socket.on('notification:read', (payload: NotificationReadPayload) => {
      console.log('[NotificationSocket] Notification read:', payload);
      this.callbacks.onNotificationRead?.(payload);
    });

    // Handle unread count update event.
    // The server's notification.gateway emits `{ count: number }` while the
    // older payload type used `{ unread_count: number }`. Read both so the
    // bell badge updates regardless of which sender is in flight.
    this.socket.on(
      'unread-count:updated',
      (payload: UnreadCountPayload | { count?: number; unread_count?: number }) => {
        console.log('[NotificationSocket] Unread count updated:', payload);
        const count =
          (payload as { count?: number })?.count ??
          (payload as UnreadCountPayload)?.unread_count;
        if (typeof count === 'number') {
          this.callbacks.onUnreadCountUpdated?.(count);
        } else {
          console.warn(
            '[NotificationSocket] unread-count:updated payload has unexpected shape:',
            payload,
          );
        }
      },
    );

    // Mark-all-as-read multi-device sync
    this.socket.on('notification:all-read', () => {
      console.log('[NotificationSocket] All read');
      this.callbacks.onAllRead?.();
    });

    // Notification cleared from bell popup on another device
    this.socket.on('notification:cleared', (payload: { notification_id: string }) => {
      console.log('[NotificationSocket] Notification cleared:', payload);
      this.callbacks.onNotificationCleared?.(payload.notification_id);
    });

    // Notification dismissed/deleted from user's view on another device
    this.socket.on('notification:dismissed', (payload: { notification_id: string }) => {
      console.log('[NotificationSocket] Notification dismissed:', payload);
      this.callbacks.onNotificationDismissed?.(payload.notification_id);
    });

    // Handle reconnection attempts
    this.socket.io.on('reconnect_attempt', (attempt) => {
      console.log('[NotificationSocket] Reconnection attempt:', attempt);
      this.callbacks.onReconnecting?.(attempt);
    });

    this.socket.io.on('reconnect', () => {
      console.log('[NotificationSocket] Reconnected');

      // Re-subscribe after reconnection
      if (this.userId) {
        this.socket?.emit('subscribe', { user_id: this.userId });
      }

      // Notify UI to re-fetch missed notifications
      this.callbacks.onReconnected?.();
    });

    this.socket.io.on('reconnect_failed', () => {
      console.error('[NotificationSocket] Reconnection failed');
      this.callbacks.onError?.(new Error('Failed to reconnect after maximum attempts'));
    });
  }

  /**
   * Re-establish the connection after a drop Socket.IO will not retry itself:
   * a server-initiated `io server disconnect`, or the built-in attempts being
   * exhausted. connect() alone is not enough — it bails on a socket that still
   * exists, and when it does proceed it would replace the dead one without
   * removing its handlers, so callbacks would fire twice per event.
   */
  reconnect(userId: string, callbacks: NotificationSocketCallbacks = {}): void {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      console.log('[NotificationSocket] Tearing down dead socket before reconnecting');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnecting = false;
    this.isHandlingAuthExpired = false;
    this.connect(userId, callbacks);
  }

  /**
   * Disconnect the socket
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[NotificationSocket] Disconnecting');
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
    this.userId = null;
    this.callbacks = {};
  }

  /**
   * Emit mark as read event to server
   */
  emitMarkAsRead(notificationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('mark-read', { notification_id: notificationId });
    }
  }

  /**
   * Emit mark all as read event to server
   */
  emitMarkAllAsRead(): void {
    if (this.socket?.connected) {
      this.socket.emit('mark-all-read');
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): 'connected' | 'connecting' | 'disconnected' {
    if (this.socket?.connected) return 'connected';
    if (this.isConnecting) return 'connecting';
    return 'disconnected';
  }

  /**
   * Update callbacks after initial connection
   */
  updateCallbacks(callbacks: Partial<NotificationSocketCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

}

// Export singleton instance
export const notificationSocketService = new NotificationSocketService();
